import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import { cn } from '@/lib/utils'
import MagnumSays from '@/components/MagnumSays'

interface LedgerEntry {
  id: number
  entryType: string
  minutes: number
  note: string | null
  createdAt: number
}

interface Session {
  id: number
  tvId: number | null
  startedAt: number
  endedAt: number | null
  minutesRequested: number | null
  minutesUsed: number | null
  expiresAt: number | null
  endReason: string | null
}

interface DailyStatus {
  balance: number
  bonusBalance: number
  budget: number | null
  isConfigured: boolean
  isUnlimited: boolean
  earnedToday: number
  redeemedToday: number
  todayEntries?: LedgerEntry[]
}

const BUDGET_PRESETS = [15, 30, 45, 60, 90, 120]

const ENTRY_LABELS: Record<string, string> = {
  EARNED: 'Earned (reward)',
  GRANTED: 'Bonus',
  REVOKED: 'Revoked',
  REDEEMED: 'Redeemed',
}

export default function ScreenTimePage() {
  const { childId } = useParams<{ childId: string }>()
  const [status, setStatus] = useState<DailyStatus | null>(null)
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [budgetInput, setBudgetInput] = useState('')
  const [savingBudget, setSavingBudget] = useState(false)
  const [bonusMinutes, setBonusMinutes] = useState(15)
  const [granting, setGranting] = useState(false)

  const load = async () => {
    try {
      const [statusData, sessData] = await Promise.all([
        apiFetch(`/screen-time/balance?childId=${childId}`),
        apiFetch(`/screen-time/sessions?childId=${childId}`),
      ])
      setStatus(statusData)
      setSessions(sessData)
      if (statusData.budget !== null && statusData.budget !== -1) {
        setBudgetInput(String(statusData.budget))
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [childId])

  const saveBudget = async (budget: number | null) => {
    setSavingBudget(true)
    try {
      const result = await apiFetch(`/screen-time/budget?childId=${childId}`, {
        method: 'PUT',
        body: JSON.stringify({ budget }),
      })
      setStatus(result)
      if (budget !== null && budget !== -1) setBudgetInput(String(budget))
    } finally {
      setSavingBudget(false)
    }
  }

  const handleGrant = async () => {
    setGranting(true)
    try {
      const result = await apiFetch(`/screen-time/grant?childId=${childId}`, {
        method: 'POST',
        body: JSON.stringify({ minutes: bonusMinutes, note: 'Parent bonus' }),
      })
      setStatus(result)
    } finally {
      setGranting(false)
    }
  }

  const handleEndSession = async (sessionId: number) => {
    await apiFetch(`/screen-time/end-session/${sessionId}`, { method: 'POST' })
    load()
  }

  if (loading || !status) {
    return <div className="max-w-lg mx-auto px-4 py-12 text-center"><p className="text-muted-foreground">Loading...</p></div>
  }

  const activeSessions = sessions.filter(s => !s.endedAt)

  return (
    <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-black text-foreground">Screen Time</h1>
        <p className="text-sm text-muted-foreground mt-1">Daily budget resets at midnight</p>
      </div>

      {/* Balance display */}
      <div className="border border-border rounded-xl p-4 text-center">
        {status.isUnlimited ? (
          <>
            <p className="text-3xl font-black text-primary">Unlimited</p>
            <p className="text-sm text-muted-foreground">Usage is tracked but not limited</p>
          </>
        ) : (
          <>
            <p className="text-4xl font-black text-primary">{status.balance}</p>
            <p className="text-sm text-muted-foreground">minutes remaining today</p>
            {status.isConfigured && status.budget != null && (
              <div className="mt-2 flex items-center justify-center gap-4 text-xs text-muted-foreground">
                <span>Daily: {status.budget} min</span>
                <span className="text-amber-600">Bonus: {status.bonusBalance} min</span>
                {status.earnedToday > 0 && <span className="text-green-600">+{status.earnedToday} earned</span>}
                {status.redeemedToday > 0 && <span className="text-red-500">-{status.redeemedToday} used</span>}
              </div>
            )}
          </>
        )}
      </div>

      {/* Daily Budget Setting */}
      <div className="border border-border rounded-xl p-4 space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Daily Budget</p>

        <div className="flex flex-wrap gap-2">
          {BUDGET_PRESETS.map(m => (
            <button key={m} onClick={() => saveBudget(m)} disabled={savingBudget}
              className={cn(
                'px-3 py-1.5 rounded-lg border text-xs font-medium transition min-h-[36px]',
                status.budget === m
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/50'
              )}>
              {m} min
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <input type="number" value={budgetInput}
            onChange={e => setBudgetInput(e.target.value)}
            placeholder="Custom"
            min={1} max={480}
            className="w-20 px-2 py-1.5 border border-border rounded-lg bg-background text-sm text-center" />
          <span className="text-xs text-muted-foreground">min/day</span>
          <button onClick={() => saveBudget(Number(budgetInput))}
            disabled={savingBudget || !budgetInput || Number(budgetInput) < 1}
            className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 transition disabled:opacity-50 min-h-[36px]">
            Set
          </button>
        </div>

        <div className="flex gap-2 pt-1 border-t border-border">
          <button onClick={() => saveBudget(-1)} disabled={savingBudget}
            className={cn(
              'px-3 py-1.5 rounded-lg border text-xs font-medium transition min-h-[36px]',
              status.isUnlimited
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/50'
            )}>
            Unlimited
          </button>
          {status.isConfigured && (
            <button onClick={() => saveBudget(null)} disabled={savingBudget}
              className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:border-red-500/50 hover:text-red-500 transition min-h-[36px]">
              Clear Budget
            </button>
          )}
        </div>
      </div>

      {/* Bonus time */}
      <div className="border border-border rounded-xl p-4 space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Add Bonus Time</p>
        <p className="text-xs text-muted-foreground">Extra minutes on top of today's daily budget</p>
        <div className="flex items-center gap-2">
          <input type="number" value={bonusMinutes}
            onChange={e => setBonusMinutes(Number(e.target.value))}
            min={1} max={480}
            className="w-16 px-2 py-1.5 border border-border rounded-lg bg-background text-sm text-center" />
          <span className="text-xs text-muted-foreground">min</span>
          <button onClick={handleGrant} disabled={granting}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:opacity-90 transition disabled:opacity-50 min-h-[36px]">
            {granting ? 'Adding...' : 'Add Bonus'}
          </button>
        </div>
      </div>

      {/* Active sessions */}
      {activeSessions.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold">Active Sessions</h2>
          {activeSessions.map(s => (
            <div key={s.id} className="flex items-center justify-between border border-green-500/20 bg-green-500/5 rounded-lg p-3 text-xs">
              <div>
                <span className="font-medium">{s.minutesRequested} min</span>
                <span className="text-muted-foreground ml-2">started {new Date(s.startedAt).toLocaleTimeString()}</span>
                {s.expiresAt && (
                  <span className="text-muted-foreground ml-1">· expires {new Date(s.expiresAt).toLocaleTimeString()}</span>
                )}
              </div>
              <button onClick={() => handleEndSession(s.id)}
                className="px-2 py-1 bg-red-100 text-red-600 rounded-md font-medium hover:bg-red-200 transition min-h-[28px]">
                End
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Today's activity */}
      {status.todayEntries && status.todayEntries.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold">Today's Activity</h2>
          {status.todayEntries.map(e => (
            <div key={e.id} className="flex items-center justify-between text-xs border-b border-border py-2">
              <div>
                <span className={`font-medium ${e.minutes > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {e.minutes > 0 ? '+' : ''}{e.minutes} min
                </span>
                <span className="text-muted-foreground ml-2">{ENTRY_LABELS[e.entryType] || e.entryType}</span>
                {e.note && <span className="text-muted-foreground ml-1">— {e.note}</span>}
              </div>
              <span className="text-muted-foreground">{new Date(e.createdAt).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent sessions */}
      {sessions.filter(s => s.endedAt).length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold">Recent Sessions</h2>
          {sessions.filter(s => s.endedAt).slice(0, 10).map(s => (
            <div key={s.id} className="flex items-center justify-between text-xs border-b border-border py-2">
              <div>
                <span className="font-medium">{s.minutesUsed ?? s.minutesRequested} min</span>
                <span className="text-muted-foreground ml-2">{new Date(s.startedAt).toLocaleDateString()}</span>
                {s.endReason && <span className="text-muted-foreground ml-1">· {s.endReason}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
